import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { CheckCircle, Clock, EnvelopeSimple, Handshake, XCircle } from '@phosphor-icons/react';
import { timeAgo } from '@/utils/datetime';

const InviteRow = ({ invite, onAccept, onDecline, processing }) => {
  const isPending = invite.status === 'pending';
  const isAccepted = invite.status === 'accepted';
  const hasInvestment = invite.investment_amount && invite.post_money_valuation;
  const investor = invite.investor || {};
  const managedFund = invite.managed_fund || {};

  const formatMoney = (value) => {
    const amount = Number(value);
    if (!amount) return '--';
    if (amount >= 10000000) return `Rs.${(amount / 10000000).toFixed(amount % 10000000 === 0 ? 0 : 1)}Cr`;
    if (amount >= 100000) return `Rs.${(amount / 100000).toFixed(amount % 100000 === 0 ? 0 : 1)}L`;
    if (amount >= 1000) return `Rs.${(amount / 1000).toFixed(amount % 1000 === 0 ? 0 : 1)}K`;
    return `Rs.${amount.toLocaleString('en-IN')}`;
  };

  const equity = hasInvestment
    ? ((Number(invite.investment_amount) / Number(invite.post_money_valuation)) * 100).toFixed(2)
    : null;

  const sectors = investor.preferred_sectors || [];
  const stages = investor.preferred_stages || [];
  const investorMeta = [
    managedFund.name || invite.managed_fund_name,
    managedFund.vintage_year ? `${managedFund.vintage_year} vintage` : null,
    investor.hq_city,
  ].filter(Boolean);

  const termItems = [
    ['Investment', formatMoney(invite.investment_amount)],
    ['Round', invite.round || '--'],
    ['Round size', formatMoney(invite.round_size)],
    ['Pre-money', formatMoney(invite.pre_money_valuation)],
    ['Post-money', formatMoney(invite.post_money_valuation)],
    ['Resulting equity', equity ? `${equity}%` : '--'],
    ['Investment year', invite.investment_year || '--'],
    ['Investor role', invite.lead_investor ? 'Lead investor' : 'Participant'],
  ];

  return (
    <div className={`rounded-xl border transition-all ${isPending ? 'border-[#0D9488]/25 bg-[#0D9488]/[0.025]' : 'border-border'}`} data-testid={`equity-invite-${invite.id}`}>
      <div className="p-3.5 sm:p-4">
        <div className="flex items-start gap-3">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${isPending ? 'bg-[#0D9488]/10 text-[#0D9488]' : isAccepted ? 'bg-emerald-500/10 text-emerald-600' : 'bg-background text-text-muted'}`}>
            {isPending ? <EnvelopeSimple size={18} weight="bold" /> : isAccepted ? <CheckCircle size={18} weight="bold" /> : <XCircle size={18} weight="bold" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-[13px] font-bold text-text-primary">{invite.vc_fund_name}</div>
              <span className={`rounded-full px-2 py-0.5 text-[8px] font-bold uppercase tracking-[0.1em] ${isPending ? 'bg-[#0D9488]/10 text-[#0D9488]' : isAccepted ? 'bg-emerald-500/10 text-emerald-600' : 'bg-background text-text-muted'}`}>
                {isPending ? 'Pending review' : isAccepted ? 'Connected' : 'Declined'}
              </span>
            </div>
            {investorMeta.length > 0 && <div className="mt-1 text-[10px] text-text-secondary">{investorMeta.join(' · ')}</div>}
            <div className="mt-1 flex items-center gap-1 text-[9px] text-text-muted"><Clock size={10} /> Sent {timeAgo(invite.created_at)}</div>
          </div>
        </div>

        {isPending ? (
          <div className="mt-4 space-y-3">
            {(investor.thesis_description || sectors.length > 0 || stages.length > 0 || investor.website || investor.typical_cheque_size) && (
              <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.02)] dark:border-border dark:bg-surface">
                <div className="mb-2 text-[9px] font-extrabold uppercase tracking-[0.12em] text-text-muted">About the investor</div>
                {investor.thesis_description && <p className="text-[10px] leading-[1.55] text-text-secondary">{investor.thesis_description}</p>}
                <div className="mt-2.5 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {sectors.length > 0 && (
                    <div>
                      <div className="text-[8px] font-bold uppercase tracking-wider text-text-muted">Sector focus</div>
                      <div className="mt-1 flex flex-wrap gap-1">{sectors.slice(0, 5).map((sector) => <span key={sector} className="rounded-md bg-surface px-1.5 py-1 text-[9px] font-medium text-text-secondary">{sector}</span>)}</div>
                    </div>
                  )}
                  {stages.length > 0 && (
                    <div>
                      <div className="text-[8px] font-bold uppercase tracking-wider text-text-muted">Stage focus</div>
                      <div className="mt-1 flex flex-wrap gap-1">{stages.slice(0, 5).map((stage) => <span key={stage} className="rounded-md bg-surface px-1.5 py-1 text-[9px] font-medium text-text-secondary">{stage}</span>)}</div>
                    </div>
                  )}
                  {investor.typical_cheque_size && <div><div className="text-[8px] font-bold uppercase tracking-wider text-text-muted">Typical cheque</div><div className="mt-0.5 text-[10px] font-semibold text-text-primary">{investor.typical_cheque_size}</div></div>}
                  {investor.lead_or_coinvest && <div><div className="text-[8px] font-bold uppercase tracking-wider text-text-muted">Investment style</div><div className="mt-0.5 text-[10px] font-semibold text-text-primary">{investor.lead_or_coinvest}</div></div>}
                </div>
                {investor.website && (
                  <a href={/^https?:\/\//i.test(investor.website) ? investor.website : `https://${investor.website}`} target="_blank" rel="noreferrer" className="mt-2.5 inline-flex text-[9px] font-bold text-[#0D9488] hover:underline">
                    View investor website ↗
                  </a>
                )}
              </div>
            )}

            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(15,23,42,0.02)] dark:border-border dark:bg-surface">
              <div className="mb-2.5 flex items-center justify-between gap-3">
                <div className="text-[9px] font-extrabold uppercase tracking-[0.12em] text-text-muted">Proposed investment terms</div>
                {equity && <div className="rounded-full bg-[#0D9488]/10 px-2 py-1 text-[9px] font-extrabold text-[#0D9488]">{equity}% ownership</div>}
              </div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                {termItems.map(([label, value]) => (
                  <div key={label}>
                    <div className="text-[8px] uppercase tracking-wider text-text-muted">{label}</div>
                    <div className={`mt-0.5 text-[10px] font-bold ${label === 'Resulting equity' && equity ? 'text-[#0D9488]' : 'text-text-primary'}`}>{value}</div>
                  </div>
                ))}
              </div>
              {invite.co_investors && <div className="mt-3 border-t border-border pt-2.5 text-[10px] text-text-secondary"><span className="font-bold text-text-primary">Co-investors:</span> {invite.co_investors}</div>}
            </div>

            {invite.personal_note && (
              <div className="rounded-lg border border-[#0D9488]/15 bg-[#0D9488]/[0.035] px-3 py-2.5">
                <div className="text-[8px] font-bold uppercase tracking-wider text-[#0D9488]">Message from {invite.vc_fund_name}</div>
                <div className="mt-1 text-[10px] italic leading-4 text-text-secondary">“{invite.personal_note}”</div>
              </div>
            )}

            <div className="flex flex-col gap-2 border-t border-border pt-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[9px] leading-4 text-text-muted sm:max-w-[55%]">Accepting connects this investor to your startup and adds the investment to your Equity holders using the terms above.</p>
              <div className="flex shrink-0 items-center gap-2">
                <button type="button" onClick={() => onDecline(invite.id)} disabled={processing} className="h-9 rounded-md border border-border px-4 text-[10px] font-bold text-text-secondary transition hover:border-rose-500/30 hover:text-rose-600 disabled:opacity-50">Decline</button>
                <button type="button" onClick={() => onAccept(invite.id)} disabled={processing} className="h-9 rounded-md bg-[#0D9488] px-4 text-[10px] font-bold text-white transition hover:bg-[#0F766E] disabled:opacity-50">Accept & add investor</button>
              </div>
            </div>
          </div>
        ) : (
          <>
            {invite.personal_note && <div className="mt-2 text-[10px] italic leading-4 text-text-secondary">“{invite.personal_note}”</div>}
            {hasInvestment && (
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 sm:grid-cols-4">
                <div><div className="text-[8px] uppercase tracking-wider text-text-muted">Investment</div><div className="mt-0.5 text-[10px] font-bold text-text-primary">{formatMoney(invite.investment_amount)}</div></div>
                <div><div className="text-[8px] uppercase tracking-wider text-text-muted">Round</div><div className="mt-0.5 text-[10px] font-bold text-text-primary">{invite.round || '--'}{invite.investment_year ? ` · ${invite.investment_year}` : ''}</div></div>
                <div><div className="text-[8px] uppercase tracking-wider text-text-muted">Post-money</div><div className="mt-0.5 text-[10px] font-bold text-text-primary">{formatMoney(invite.post_money_valuation)}</div></div>
                <div><div className="text-[8px] uppercase tracking-wider text-text-muted">Equity</div><div className="mt-0.5 text-[10px] font-bold text-[#0D9488]">{equity}%</div></div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const EquityInvitesCard = ({ onInviteAccepted }) => {
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [processing, setProcessing] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/startup/invites');
      setInvites(res.data || []);
    } catch (err) {
      console.error('Failed to fetch startup invites', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAccept = async (inviteId) => {
    setProcessing(true);
    try {
      const res = await api.post(`/startup/invites/${inviteId}/accept`, {});
      toast.success(`Connected with ${res.data.vc_fund_name}!`);
      await Promise.all([load(), onInviteAccepted?.()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to accept invite');
    } finally {
      setProcessing(false);
    }
  };

  const handleDecline = async (inviteId) => {
    setProcessing(true);
    try {
      await api.post(`/startup/invites/${inviteId}/decline`, {});
      toast.success('Invite declined');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to decline invite');
    } finally {
      setProcessing(false);
    }
  };

  const pending = invites.filter((invite) => invite.status === 'pending');
  const history = invites.filter((invite) => invite.status !== 'pending');

  return (
    <section className="mt-3 rounded-xl border border-border p-4 sm:p-5" style={{ backgroundColor: 'var(--card)' }} data-testid="equity-invites-card">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-1 text-[10px] font-extrabold uppercase tracking-[0.15em] text-[#0D9488]">Investor connections</div>
          <h2 className="font-heading text-[18px] font-bold tracking-[-0.02em] text-text-primary">Invites</h2>
          <p className="mt-1 text-[11px] leading-5 text-text-muted">Review requests from VCs that want to add your startup to their portfolio.</p>
        </div>
        {pending.length > 0 && <span className="rounded-full bg-[#0D9488]/10 px-2.5 py-1 text-[10px] font-bold text-[#0D9488]">{pending.length} pending</span>}
      </div>

      {loading ? (
        <div className="h-24 animate-pulse rounded-lg bg-background" />
      ) : invites.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border px-4 py-8 text-center">
          <Handshake size={24} className="mx-auto text-text-muted" />
          <div className="mt-2 text-[11px] font-bold text-text-primary">No investor invites yet</div>
          <div className="mt-1 text-[10px] text-text-muted">New portfolio invites will appear here when a VC sends one.</div>
        </div>
      ) : (
        <div className="space-y-4">
          {pending.length > 0 && (
            <div>
              <div className="mb-2 text-[9px] font-bold uppercase tracking-[0.12em] text-text-muted">Pending</div>
              <div className="space-y-2">{pending.map((invite) => <InviteRow key={invite.id} invite={invite} onAccept={handleAccept} onDecline={handleDecline} processing={processing} />)}</div>
            </div>
          )}
          {history.length > 0 && (
            <div>
              <div className="mb-2 text-[9px] font-bold uppercase tracking-[0.12em] text-text-muted">History</div>
              <div className="space-y-2">{history.map((invite) => <InviteRow key={invite.id} invite={invite} onAccept={handleAccept} onDecline={handleDecline} processing={processing} />)}</div>
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default EquityInvitesCard;
